import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BackendApi } from '../../../api/api.ts';
import { fetchDocumentContentBlob } from '../../../api/fetchDocumentContent.ts';
import {
    usePartner__client__bill__listBillTodos,
    usePartner__client__getDocument,
    useReconciliation__getMatchDetailsByDocumentId,
} from '../../../generated/blueprint.ts';
import type { BillTodoType } from '../../../generated/enums.ts';
import { usePossibleDuplicates } from '../../../hooks/usePossibleDuplicates.ts';
import type { BillDuplicate } from '../../../components/specific/PossibleDuplicates/possibleDuplicates.ts';
import type { BillTodo } from '../types/billTodo.ts';
import type { DocumentDrawerActions } from '../DocumentDrawerProvider.tsx';
import { QueryKey } from '../../../api/queryKeys.ts';
import type { RecategorizeCategoryType } from '../../../types/enums.ts';
import type { MatchBillPart, TFICDocument } from '../types/Document.ts';
import type { MatchData } from '../types/data.ts';
import type { MatchTransactionPart } from '../../../pages/app/partner/pages/partnerUnmatchedTransactionsPage/types.ts';
import type { DocumentSavePayload } from '../utils/documentSave.ts';

const DOCUMENT_CONTENT_QUERY = 'document-content';
const api = new BackendApi();

const drawerQueryOptions = {
    gcTime: 0,
    refetchOnWindowFocus: false,
} as const;

export function documentQueryKey(businessId: string, documentId: string) {
    return [QueryKey.DocumentDrawer, 'partner/client/get-document', {business_id: businessId, document_id: documentId}];
}

export function useDocument(businessId: string | null, documentId: string | null) {
    return usePartner__client__getDocument(api, {business_id: businessId as string, document_id: documentId as string}, {
        queryKey: QueryKey.DocumentDrawer,
        select: (response) => response as unknown as TFICDocument,
        enabled: !!businessId && !!documentId,
        ...drawerQueryOptions,
    });
}

export function useDocumentLoadFailed(businessId: string | null, documentId: string | null) {
    const queryClient = useQueryClient();
    const [loadFailed, setLoadFailed] = useState(false);
    useEffect(() => {
        setLoadFailed(false);
        if (!businessId || !documentId) {
            return;
        }
        let cancelled = false;
        const query = queryClient.getQueryCache().find({
            queryKey: documentQueryKey(businessId, documentId),
            exact: true,
        });
        const fetchPromise = query?.promise;
        if (!fetchPromise) {
            return;
        }
        fetchPromise.then(
            () => {
                if (!cancelled) {
                    setLoadFailed(false);
                }
            },
            () => {
                if (!cancelled) {
                    setLoadFailed(true);
                }
            },
        );
        return () => {
            cancelled = true;
        };
    }, [queryClient, businessId, documentId]);
    return loadFailed;
}

export function useDocumentContent(businessId: string | null, documentId: string | null) {
    return useQuery({
        queryKey: [DOCUMENT_CONTENT_QUERY, businessId, documentId],
        queryFn: ({ signal }) =>
            fetchDocumentContentBlob(
                api.withAbortSignal(signal),
                'partner/client/get-document-content',
                {
                    business_id: businessId as string,
                    document_id: documentId as string,
                },
            ),
        enabled: !!businessId && !!documentId,
        ...drawerQueryOptions,
    });
}

export function useMatchDetails(documentId: string | null) {
    return useReconciliation__getMatchDetailsByDocumentId(api, {document_id: documentId as string}, {
        queryKey: QueryKey.DocumentDrawer,
        select: (response) => ({
            matchId: response.match_id,
            bills: response.bill_parts as unknown as MatchBillPart[],
            transactions: response.transaction_parts as unknown as MatchTransactionPart[],
            isCompleted: response.is_completed,
            nrsRemainder: response.nrs_remainder,
            createdAt: response.created_at,
            completedAt: response.completed_at,
            matchingReason: response.matching_reason,
            createdByName: response.created_by_name,
            differenceBookingDate: response.difference_booking_date,
            differenceBookingReason: response.difference_booking_reason,
            differenceBookingGlaId: response.difference_booking_gla_id,
            differenceBookingGlaLabel: response.difference_booking_gla_label,
        } satisfies MatchData),
        enabled: !!documentId,
        ...drawerQueryOptions,
    });
}

const NO_TODOS: BillTodo[] = [];

export function useBillTodos(billId: string | null) {
    return usePartner__client__bill__listBillTodos(api, {bill_id: billId as string}, {
        queryKey: QueryKey.DocumentDrawer,
        select: (response) => response.todos,
        enabled: !!billId,
        ...drawerQueryOptions,
    });
}

export async function fetchBillTodos(billId: string | null): Promise<BillTodo[]> {
    if (!billId) {
        return NO_TODOS;
    }
    const response = await api.Partner__client__bill__listBillTodos(billId);
    return response.todos;
}

export function dismissBillTodo(billId: string, todoType: BillTodoType) {
    return api.Partner__client__bill__dismissTodo({ bill_id: billId, todo_type: todoType });
}

export function useDrawerPossibleDuplicates(
    openDocument: DocumentDrawerActions['open'],
    onDeleted: (bill: BillDuplicate) => Promise<void> | void,
) {
    return usePossibleDuplicates(api, { openDocument, onDeleted, closeOnView: true });
}

export function saveDocument(payload: DocumentSavePayload) {
    return api.Partner__client__document__editDocumentFields(payload);
}

export function convertBillToEuro(documentId: string) {
    return api.Partner__document__bill__convertToEuro({ document_id: documentId });
}

export function deleteDocument(documentId: string) {
    return api.Partner__client__document__delete({ document_id: documentId });
}

export function recategorizeDocument(documentId: string, category: RecategorizeCategoryType) {
    return api.Partner__recategorizeDocument({ document_id: documentId, category });
}

export function unmatchDocument(matchId: string, billIds: string[], transactionIds: string[]) {
    return api.Reconciliation__manuallyUnmatch({
        match_id: matchId,
        bill_ids: billIds,
        transaction_ids: transactionIds,
    });
}
